/**
 * Info tooltip component - displays a ? icon with hover tooltip
 */
import { useState, useRef, useEffect } from 'react'

interface InfoTooltipProps {
  content: string
  position?: 'top' | 'bottom'
  maxWidth?: number
}

/**
 * A small info icon that shows a tooltip on hover
 *
 * @example
 * <label>
 *   Target Allocation
 *   <InfoTooltip content={TOOLTIPS.targetAllocation} />
 * </label>
 */
export function InfoTooltip({ content, position = 'top', maxWidth = 250 }: InfoTooltipProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [adjustedPosition, setAdjustedPosition] = useState(position)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const iconRef = useRef<HTMLSpanElement>(null)

  // Check if tooltip would go off screen and adjust
  useEffect(() => {
    if (isVisible && tooltipRef.current) {
      const tooltipRect = tooltipRef.current.getBoundingClientRect()

      if (position === 'top' && tooltipRect.top < 0) {
        setAdjustedPosition('bottom')
      } else if (position === 'bottom' && tooltipRect.bottom > window.innerHeight) {
        setAdjustedPosition('top')
      } else {
        setAdjustedPosition(position)
      }
    }
  }, [isVisible, position])

  return (
    <span
      ref={iconRef}
      className="relative inline-flex items-center ml-1"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {/* Question mark icon */}
      <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-200 text-gray-500 text-xs cursor-help hover:bg-gray-300 transition-colors">
        ?
      </span>

      {/* Tooltip */}
      {isVisible && (
        <div
          ref={tooltipRef}
          className={`absolute z-50 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg ${
            adjustedPosition === 'top'
              ? 'bottom-full mb-2 left-1/2 -translate-x-1/2'
              : 'top-full mt-2 left-1/2 -translate-x-1/2'
          }`}
          style={{ maxWidth, width: 'max-content' }}
        >
          {content}
          {/* Arrow */}
          <span
            className={`absolute left-1/2 -translate-x-1/2 w-0 h-0 border-4 border-transparent ${
              adjustedPosition === 'top'
                ? 'top-full border-t-gray-900'
                : 'bottom-full border-b-gray-900'
            }`}
          />
        </div>
      )}
    </span>
  )
}
